import { chatResourceKey } from "./model.ts";
import { useChat, useChatReview, useChatScroll } from "./react.tsx";
import { useCallback, useMemo, type ReactNode } from "react";
import { Effect } from "effect";
import { Button } from "@cloudflare/kumo";
import { Plus, Warning } from "@phosphor-icons/react";
import { CHAT_MODELS, chatModelOption, type ChatMessage, type ChatProposalPart } from "@workerstation/shared";

import { OpencodeThemeProvider, useOpencodeTheme } from "../opencode/index.ts";
import { SessionTurn } from "./session-turn.tsx";
import {
  AssistantMessage,
  MessagePartView,
  UserMessage
} from "./message-part.tsx";
import { renderable, toTranscriptParts } from "../opencode/part-adapter.ts";
import { SessionProgressIndicator } from "../opencode/session-progress-indicator.tsx";
import { PromptInput, type Attachment } from "./PromptInput.tsx";



export interface ChatSurfaceProps {
  readonly stationId: string;
  readonly workspaceId: string | null;
  // Called after a proposal merge lands so the workbench rehydrates the
  // workspace: the editor has no other invalidation channel.
  readonly onWorkspaceInvalidated: () => void;
}

export function ChatSurface(props: ChatSurfaceProps) {
  return (
    <OpencodeThemeProvider>
      <ChatSurfaceInner key={props.stationId} {...props} />
    </OpencodeThemeProvider>
  );
}

function ChatSurfaceInner({
  stationId,
  workspaceId,
  onWorkspaceInvalidated
}: ChatSurfaceProps) {
  const { themeId, setThemeId, availableThemes } = useOpencodeTheme();

  const invalidation = useMemo(() => Effect.sync(onWorkspaceInvalidated), [onWorkspaceInvalidated]);
  const chat = useChat({ stationId, invalidate: invalidation });
  const { config, threads, threadId, session, turnActive, draft, error, threadError, tailError, stopping,
    creating: busy, configuring: configBusy, approving, resolving } = chat.snapshot;
  const { dispatch } = chat;
  const scroll = useChatScroll(session);
  const send = useCallback((text: string, attachments: Attachment[]) => chat.session && threadId
    ? chat.session.send({ stationId, threadId, text, attachments }) : Effect.succeed(false), [chat.session, stationId, threadId]);
  const attachmentError = useCallback((message: string) => dispatch({ _tag: "AttachmentError", threadId, message }), [dispatch, threadId]);

  const renderProposalReview = useCallback((proposal: ChatProposalPart) => threadId ? (
    <ProposalWorktreeChanges
      key={JSON.stringify([proposal.proposalId, proposal.worktreeId])}
      stationId={stationId}
      threadId={threadId}
      proposalId={proposal.proposalId}
      worktreeId={proposal.worktreeId}
    />
  ) : null, [stationId, threadId]);

  const updateDraft = (threadId: string, text: string) => dispatch({ _tag: "Draft", threadId, text });
  const startThread = () => dispatch({ _tag: "Create" });
  const setModel = (model: string) => dispatch({ _tag: "Configure", patch: { model } });
  const setYolo = (yolo: boolean) => dispatch({ _tag: "Configure", patch: { yolo } });
  const stop = () => { if (threadId) dispatch({ _tag: "Stop", threadId }); };
  const decideApproval = (approvalId: string, approve: boolean) => { if (threadId) dispatch({ _tag: "Approval", threadId, approvalId, approve }); };
  const resolveProposal = (proposalId: string, action: "merge" | "discard") => { if (threadId) dispatch({ _tag: "Proposal", threadId, proposalId, action }); };

  const providerUnavailable = config?.configured === false;
  const displayedError =
    error ?? (threadError?.threadId === threadId ? threadError.message : null) ?? tailError;

  return (
    <div className="flex h-full flex-col">
      <div data-component="chat-header">
        <select
          aria-label="Chat thread"
          data-component="chat-title-select"
          value={threadId ?? ""}
          onChange={(event) => {
            scroll.resume();
            dispatch({ _tag: "Select", threadId: event.target.value || null });
          }}
        >
          {threads.length === 0 ? <option value="">No threads yet</option> : null}
          {threads.map((thread) => (
            <option key={thread.threadId} value={thread.threadId}>
              {thread.title}
            </option>
          ))}
        </select>
        <select
          aria-label="Theme"
          data-component="composer-select"
          value={themeId}
          onChange={(event) => setThemeId(event.target.value)}
        >
          {availableThemes.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>

        {(() => {
          const estimatedTokens = Math.round(
            session.messages.reduce(
              (acc, m) =>
                acc +
                (m.role === "user"
                  ? m.text.length
                  : m.parts.reduce(
                      (a, p) =>
                        a +
                        (p.kind === "text" || p.kind === "reasoning" ? p.text.length : 10),
                      0
                    )),
              0
            ) / 4
          );
          const maxTokens = 128000;
          const percent = Math.min(100, Math.round((estimatedTokens / maxTokens) * 100));
          return (
            <div
              data-component="context-usage-meter"
              aria-label="Context usage"
              title={`Context usage: ${estimatedTokens.toLocaleString()} / ${maxTokens.toLocaleString()} tokens (${percent}%)`}
            >
              <span>{percent}%</span>
              <div className="h-1.5 w-8 overflow-hidden rounded-full bg-kumo-fill">
                <div className="h-full bg-sky-500" style={{ width: `${percent}%` }} />
              </div>
            </div>
          );
        })()}

        <Button
          variant="ghost"
          shape="square"
          aria-label="New chat thread"
          title="New chat thread"
          disabled={busy || providerUnavailable}
          onClick={() => void startThread()}
          icon={<Plus size={13} />}
          className="h-6 w-6 text-kumo-subtle hover:text-kumo-default"
        />
      </div>

      {providerUnavailable ? (
        <div className="flex shrink-0 items-start gap-2 border-b border-kumo-line px-3 py-3">
          <Warning size={18} className="shrink-0 text-kumo-subtle" />
          <div>
            <p className="text-sm font-medium text-kumo-default">Chat is not configured</p>
            <p className="text-[12px] text-kumo-subtle">
              No model provider is available on this deployment. Set the
              <code className="mx-1 rounded bg-kumo-fill px-1">AI_GATEWAY_CLIENT_KEY</code>,
              <code className="mx-1 rounded bg-kumo-fill px-1">CF_AIG_TOKEN</code>, and
              <code className="mx-1 rounded bg-kumo-fill px-1">AI_GATEWAY_URL</code>
              secrets to enable the station chat agent. Existing threads remain readable.
            </p>
          </div>
        </div>
      ) : null}

      <div
        ref={scroll.ref}
        data-component="chat-transcript"
        onScroll={scroll.onScroll}
      >
        {session.messages.length === 0 ? (
          <p data-component="chat-empty">
            Ask about station <code>{stationId}</code>
            {workspaceId ? (
              <>
                {" "}
                (workspace <code>{workspaceId}</code>)
              </>
            ) : null}
            . The agent can inspect files, run tests and previews, read traces, and
            propose edits you review before they land.
          </p>
        ) : null}
        {turnsOf(session.messages).map((turn) => (
          <ChatTurnView
            key={JSON.stringify([threadId, turn.key])}
            turn={turn}
            threadId={threadId}
            approving={approving}
            resolving={resolving}
            onApproval={providerUnavailable ? undefined : decideApproval}
            onProposal={providerUnavailable ? undefined : resolveProposal}
            renderProposalReview={renderProposalReview}
          />
        ))}
        {turnActive ? (
          <div data-component="chat-progress">
            <SessionProgressIndicator />
          </div>
        ) : null}
      </div>

      {displayedError ? (
        <p role="alert" className="border-t border-kumo-line px-2 py-1 text-[12px] text-red-500">
          {displayedError}
        </p>
      ) : null}

      <div data-component="composer">
        {/* Model and mode are per-station settings, the same scope the agent
            already reads them from when it runs a turn. */}
        <div data-component="composer-settings">
          <label className="sr-only" htmlFor="chat-model">
            Model
          </label>
          <select
            id="chat-model"
            aria-label="Model"
            value={config?.model ?? ""}
            onChange={(event) => void setModel(event.target.value)}
            disabled={configBusy || !config || providerUnavailable}
            data-component="composer-select"
          >
            {/* A station may hold a model that predates this catalog, so its
                current value is always offered even if unlisted. */}
            {[
              ...CHAT_MODELS,
              ...(config?.model && !chatModelOption(config.model)
                ? [{ id: config.model, label: config.model }]
                : [])
            ].map((model) => (
              <option key={model.id} value={model.id}>
                {model.label}
              </option>
            ))}
          </select>
          <label className="sr-only" htmlFor="chat-mode">
            Mode
          </label>
          <select
            id="chat-mode"
            aria-label="Mode"
            value={config?.yolo ? "yolo" : "default"}
            onChange={(event) => void setYolo(event.target.value === "yolo")}
            disabled={configBusy || !config || providerUnavailable}
            title="Default asks before destructive tools. YOLO runs every tool without asking."
            data-component="composer-select"
            data-mode={config?.yolo ? "yolo" : "default"}
          >
            <option value="default">Default</option>
            <option value="yolo">YOLO</option>
          </select>
        </div>
        <PromptInput
          scopeKey={threadId ?? ""}
          value={draft}
          onChange={(value) => {
            if (threadId) updateDraft(threadId, value);
          }}
          onSend={send}
          onStop={() => void stop()}
          stopDisabled={stopping || providerUnavailable}
          onAttachmentError={attachmentError}
          disabled={!threadId || providerUnavailable}
          busy={turnActive}
          placeholder={threadId ? "Ask about this station…" : "Start a thread first"}
        />
      </div>
    </div>
  );
}

const changeLabels = {
  added: "Added", modified: "Modified", deleted: "Deleted", renamed: "Renamed",
  "workspace-added": "Workspace added", "workspace-deleted": "Workspace deleted",
  "workspace-order-changed": "Workspace order", "active-workspace-changed": "Active workspace",
  folders: "Folders", openTabs: "Open tabs", currentFile: "Current file", options: "Options",
  schemaVersion: "Schema version", bindings: "Bindings", migrations: "Migrations", configuration: "Configuration"
} as const;

function changedValue(value: unknown): string {
  return value === undefined ? "(none)" : typeof value === "string" ? value : JSON.stringify(value);
}

// The proposal session owns its on-demand read and cancels it on removal.
function ProposalWorktreeChanges({ stationId, threadId, proposalId, worktreeId }: { stationId: string; threadId: string; proposalId: string; worktreeId: string }) {
  const { snapshot: state, load } = useChatReview({ stationId, threadId, proposalId, worktreeId });

  const diff = state.status === "loaded" ? state.response.diff : undefined;
  const groups = diff ? [
    { name: "Files", total: diff.totals.files, changes: diff.files.map(file => (
      <><span>{changeLabels[file.kind]}</span> · <span>{file.workspaceId}</span> / {file.previousPath ? <><code>{file.previousPath}</code> → </> : null}<code>{file.path}</code></>
    )) },
    { name: "Workspaces", total: diff.totals.topology, changes: diff.topology.map(change => (
      <><span>{changeLabels[change.kind]}</span>{change.workspaceId ? <> · {change.workspaceId}</> : null}
        {change.before !== undefined || change.after !== undefined ? <div><del><code>{changedValue(change.before)}</code></del> → <ins><code>{changedValue(change.after)}</code></ins></div> : null}</>
    )) },
    { name: "Workspace settings", total: diff.totals.configuration, changes: diff.configuration.map(change => (
      <><span>{changeLabels[change.field]}</span> · {change.workspaceId}
        <div><del><code>{changedValue(change.before)}</code></del> → <ins><code>{changedValue(change.after)}</code></ins></div></>
    )) },
    { name: "Station settings", total: diff.totals.stationConfiguration, changes: diff.stationConfiguration.map(change => (
      <><span>{changeLabels[change.field]}</span>
        <div><del><code>{changedValue(change.before)}</code></del> → <ins><code>{changedValue(change.after)}</code></ins></div></>
    )) }
  ] : [];
  const shown = groups.reduce((sum, group) => sum + group.changes.length, 0);
  const total = groups.reduce((sum, group) => sum + group.total, 0);
  return (
    <section data-component="proposal-worktree-changes" aria-label="Current worktree changes">
      <div data-component="tool-approval">
        <button type="button" disabled={state.status === "loading"} onClick={() => load()}>
          {state.status === "idle" ? "View worktree changes" : state.status === "loading" ? "Loading changes…" : state.status === "error" ? "Retry changes" : "Refresh changes"}
        </button>
      </div>
      {state.status === "error" ? <p role="alert">Could not load worktree changes. Try again.</p> : null}
      {state.status === "loaded" ? (
        <div aria-live="polite">
          <h4>Current worktree changes</h4>
          <p>Worktree version {state.response.worktreeVersion}</p>
          <p>This view lists changed files and settings.</p>
          {diff?.truncated ? <p>Partial result: showing {shown} of {total} changes.</p> : null}
          {total === 0 ? <p>No current worktree changes.</p> : null}
          {groups.filter(group => group.total > 0).map(group => (
            <div key={group.name}>
              <h5>{group.name} ({group.changes.length} of {group.total})</h5>
              <ul>{group.changes.map((change, index) => <li key={index}>{change}</li>)}</ul>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

interface ChatTurn {
  readonly key: string;
  readonly prompt?: Extract<ChatMessage, { role: "user" }>;
  readonly reply?: Extract<ChatMessage, { role: "assistant" }>;
}

/**
 * Groups the flat message list into turns.
 *
 * Our read model keeps user and assistant messages as siblings; opencode's `session-turn`
 * owns a prompt *and* its reply, and its stylesheet reaches assistant content through the
 * turn's container. Pairing them here is what lets the vendored CSS lay the turn out —
 * rendering each message as its own turn puts every reply in an empty shell.
 *
 * An assistant message with no preceding prompt still gets a turn, so a resumed thread
 * that starts mid-reply is not dropped.
 */
export function turnsOf(messages: readonly ChatMessage[]): ChatTurn[] {
  const turns: ChatTurn[] = [];
  for (const message of messages) {
    if (message.role === "user") {
      turns.push({ key: message.id, prompt: message });
      continue;
    }
    const open = turns[turns.length - 1];
    if (open && open.reply === undefined) {
      turns[turns.length - 1] = { ...open, reply: message };
      continue;
    }
    turns.push({ key: message.id, reply: message });
  }
  return turns;
}

function ChatTurnView({
  turn,
  threadId,
  approving,
  resolving,
  onApproval,
  onProposal,
  renderProposalReview
}: {
  turn: ChatTurn;
  threadId: string | null;
  approving: readonly string[];
  resolving: readonly string[];
  onApproval?: (approvalId: string, approve: boolean) => void;
  onProposal?: (proposalId: string, action: "merge" | "discard") => void;
  renderProposalReview: (proposal: ChatProposalPart) => ReactNode;
}) {
  const reply = turn.reply;
  const parts = reply ? toTranscriptParts(reply.parts).filter(renderable) : [];
  const working = reply?.open ?? false;
  const turnEnd = reply?.turnEnd;
  const interactive = parts.some(part => part.type === "proposal"
    || part.type === "tool" && part.state.status === "awaiting_approval" && onApproval !== undefined);

  return (
    <SessionTurn
      working={working}
      interactive={interactive}
      message={
        turn.prompt === undefined ? undefined : (
          <UserMessage
            text={turn.prompt.text}
            attachments={turn.prompt.attachments}
          />
        )
      }
    >
      {parts.length > 0 || turnEnd ? (
        <AssistantMessage>
          {parts.map((part) => (
            <MessagePartView
              key={part.id}
              part={part}
              streaming={working}
              onApproval={onApproval}
              isApprovalPending={id => approving.includes(chatResourceKey("approval", threadId, id))}
              isProposalPending={id => resolving.includes(chatResourceKey("proposal", threadId, id))}
              onProposal={onProposal}
              renderProposalReview={renderProposalReview}
            />
          ))}
          {turnEnd && turnEnd.reason !== "completed" ? (
            <p data-component="turn-end">{turnEnd.detail ?? turnEnd.reason}</p>
          ) : null}
        </AssistantMessage>
      ) : null}
    </SessionTurn>
  );
}
